import { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { useConceptGate } from '../../../../../context/ConceptGateContext';
import { Direction } from '../../../../../api/conceptgate';

/**
 * Hook to display ConceptGate trade entry/stop/target lines on the chart.
 * Lines update automatically when currentTrade changes.
 * Supports drag-to-edit: hover near a line to see grab cursor, drag to move.
 */

// Pixel threshold for detecting hover/click near a line
const LINE_PROXIMITY_THRESHOLD = 6;
const TOUCH_LINE_PROXIMITY_THRESHOLD = 14;

export const useTradePriceLines = (
  candleSeriesRef,
  chartRef,
  containerRef,
  chartReady = true
) => {
  const { currentTrade, updateTradeField } = useConceptGate();

  // Store refs to created lines for cleanup
  const linesRef = useRef({
    entry: null,
    stop: null,
    target: null,
  });

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [draggingLine, setDraggingLine] = useState(null); // 'entry' | 'stop' | 'target'
  const [hoveringLine, setHoveringLine] = useState(null);
  const [selectedLine, setSelectedLine] = useState(null);
  const dragStartPriceRef = useRef(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    setIsTouchDevice(
      typeof window !== 'undefined'
      && ('ontouchstart' in window || navigator.maxTouchPoints > 0)
    );
  }, []);

  // Line colors and styles (memoized to avoid dependency issues)
  const lineConfig = useMemo(() => ({
    entry: {
      color: '#2196F3', // Blue
      lineWidth: 2,
      lineStyle: 2, // Dashed
      title: 'Entry',
    },
    stop: {
      color: '#EF4444', // Red
      lineWidth: 2,
      lineStyle: 2,
      title: 'Stop',
    },
    target: {
      color: '#22C55E', // Green
      lineWidth: 2,
      lineStyle: 2,
      title: 'Target',
    },
  }), []);

  // Remove all trade lines
  const clearLines = useCallback(() => {
    const series = candleSeriesRef?.current;
    if (!series) return;

    Object.entries(linesRef.current).forEach(([key, line]) => {
      if (line) {
        try {
          series.removePriceLine(line);
        } catch (e) {
          // Line may already be removed
        }
        linesRef.current[key] = null;
      }
    });
  }, [candleSeriesRef]);

  // Create or update a single line
  const updateLine = useCallback((type, price, config) => {
    const series = candleSeriesRef?.current;
    if (!series || price === null || price === undefined) return;

    // Remove existing line of this type
    if (linesRef.current[type]) {
      try {
        series.removePriceLine(linesRef.current[type]);
      } catch (e) {
        // Line may already be removed
      }
    }

    // Create new line
    const line = series.createPriceLine({
      price: parseFloat(price),
      color: config.color,
      lineWidth: config.lineWidth,
      lineStyle: config.lineStyle,
      axisLabelVisible: true,
      title: config.title,
    });

    linesRef.current[type] = line;
  }, [candleSeriesRef]);

  // Get the Y coordinate for a given price
  const priceToY = useCallback((price) => {
    const series = candleSeriesRef?.current;
    if (!series || price === null || price === undefined) return null;
    try {
      return series.priceToCoordinate(parseFloat(price));
    } catch (e) {
      return null;
    }
  }, [candleSeriesRef]);

  // Get the price for a given Y coordinate
  const yToPrice = useCallback((y) => {
    const series = candleSeriesRef?.current;
    if (!series) return null;
    try {
      return series.coordinateToPrice(y);
    } catch (e) {
      return null;
    }
  }, [candleSeriesRef]);

  // Find which line (if any) is near the given Y coordinate
  const findLineNearY = useCallback((y, threshold = LINE_PROXIMITY_THRESHOLD) => {
    if (!currentTrade) return null;

    const lines = [
      { type: 'entry', price: currentTrade.entryPrice },
      { type: 'stop', price: currentTrade.stopPrice },
      { type: 'target', price: currentTrade.targetPrice },
    ];

    let closestLine = null;
    let closestDistance = Infinity;

    for (const line of lines) {
      if (line.price === null || line.price === undefined) continue;
      const lineY = priceToY(line.price);
      if (lineY === null) continue;

      const distance = Math.abs(lineY - y);
      if (distance <= threshold && distance < closestDistance) {
        closestLine = line.type;
        closestDistance = distance;
      }
    }

    return closestLine;
  }, [currentTrade, priceToY]);

  // Handle mouse move - detect hovering near lines
  const handleMouseMove = useCallback((e) => {
    if (!chartReady || !currentTrade || !containerRef?.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;

    if (isDragging && draggingLine) {
      // During drag, update the line preview
      const newPrice = yToPrice(y);
      if (newPrice !== null) {
        // Round to 5 decimal places
        const roundedPrice = Math.round(newPrice * 100000) / 100000;

        // Update the line visually (without committing to context yet)
        const line = linesRef.current[draggingLine];
        if (line) {
          line.applyOptions({ price: roundedPrice });
        }
      }
    } else {
      // Not dragging - check for hover near any part of the line
      const nearLine = findLineNearY(y);
      setHoveringLine(nearLine);

      // Update cursor - show drag cursor if near line
      if (containerRef.current) {
        containerRef.current.style.cursor = nearLine ? 'ns-resize' : 'default';
      }
    }
  }, [chartReady, currentTrade, containerRef, isDragging, draggingLine, yToPrice, findLineNearY]);

  // Handle mouse down - start drag if near a line
  const handleMouseDown = useCallback((e) => {
    if (isTouchDevice) return;
    if (!chartReady || !currentTrade || !containerRef?.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;

    const nearLine = findLineNearY(y);
    if (nearLine) {
      e.preventDefault();
      e.stopPropagation();

      // Disable chart panning while dragging line
      if (chartRef?.current) {
        chartRef.current.applyOptions({
          handleScroll: false,
          handleScale: false,
        });
      }

      setIsDragging(true);
      setDraggingLine(nearLine);
      dragStartPriceRef.current = currentTrade[`${nearLine}Price`];

      // Change cursor
      if (containerRef.current) {
        containerRef.current.style.cursor = 'ns-resize';
      }
    }
  }, [chartReady, currentTrade, containerRef, chartRef, findLineNearY, isTouchDevice]);

  // Handle mouse up - commit drag
  const handleMouseUp = useCallback((e) => {
    if (isTouchDevice) return;
    if (!isDragging || !draggingLine || !containerRef?.current) {
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const newPrice = yToPrice(y);

    if (newPrice !== null) {
      // Round to 5 decimal places
      const roundedPrice = Math.round(newPrice * 100000) / 100000;

      // Map line type to field name
      const fieldMap = {
        entry: 'entryPrice',
        stop: 'stopPrice',
        target: 'targetPrice',
      };

      // Commit the change to context
      updateTradeField(fieldMap[draggingLine], roundedPrice);
    }

    // Re-enable chart panning
    if (chartRef?.current) {
      chartRef.current.applyOptions({
        handleScroll: true,
        handleScale: true,
      });
    }

    setIsDragging(false);
    setDraggingLine(null);
    dragStartPriceRef.current = null;

    // Reset cursor
    if (containerRef.current) {
      containerRef.current.style.cursor = 'default';
    }
  }, [isDragging, draggingLine, containerRef, chartRef, yToPrice, updateTradeField, isTouchDevice]);

  // Handle mouse leave - cancel drag if mouse leaves chart
  const handleMouseLeave = useCallback(() => {
    if (isTouchDevice) return;
    if (isDragging && draggingLine && dragStartPriceRef.current !== null) {
      // Restore original price
      const line = linesRef.current[draggingLine];
      if (line) {
        line.applyOptions({ price: dragStartPriceRef.current });
      }

      // Re-enable chart panning
      if (chartRef?.current) {
        chartRef.current.applyOptions({
          handleScroll: true,
          handleScale: true,
        });
      }
    }

    setIsDragging(false);
    setDraggingLine(null);
    setHoveringLine(null);
    dragStartPriceRef.current = null;

    if (containerRef?.current) {
      containerRef.current.style.cursor = 'default';
    }
  }, [isDragging, draggingLine, containerRef, chartRef, isTouchDevice]);

  const handleTouchStart = useCallback((e) => {
    if (!chartReady || !currentTrade || !containerRef?.current) return;
    const touch = e.touches[0];
    if (!touch) return;

    const rect = containerRef.current.getBoundingClientRect();
    const y = touch.clientY - rect.top;
    const nearLine = findLineNearY(y, TOUCH_LINE_PROXIMITY_THRESHOLD);

    if (nearLine && nearLine !== selectedLine) {
      setSelectedLine(nearLine);
      return;
    }

    if (!selectedLine || !nearLine) {
      return;
    }

    e.preventDefault();

    if (chartRef?.current) {
      chartRef.current.applyOptions({
        handleScroll: false,
        handleScale: false,
      });
    }

    setIsDragging(true);
    setDraggingLine(selectedLine);
    dragStartPriceRef.current = currentTrade[`${selectedLine}Price`];
  }, [chartReady, currentTrade, containerRef, chartRef, findLineNearY, selectedLine]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging || !draggingLine || !containerRef?.current) return;
    const touch = e.touches[0];
    if (!touch) return;
    e.preventDefault();

    const rect = containerRef.current.getBoundingClientRect();
    const y = touch.clientY - rect.top;
    const newPrice = yToPrice(y);

    if (newPrice !== null) {
      const roundedPrice = Math.round(newPrice * 100000) / 100000;
      const line = linesRef.current[draggingLine];
      if (line) {
        line.applyOptions({ price: roundedPrice });
      }
    }
  }, [isDragging, draggingLine, containerRef, yToPrice]);

  const handleTouchEnd = useCallback((e) => {
    if (!isDragging || !draggingLine || !containerRef?.current) {
      return;
    }

    const touch = e.changedTouches[0];
    if (!touch) return;

    const rect = containerRef.current.getBoundingClientRect();
    const y = touch.clientY - rect.top;
    const newPrice = yToPrice(y);

    if (newPrice !== null) {
      const roundedPrice = Math.round(newPrice * 100000) / 100000;

      const fieldMap = {
        entry: 'entryPrice',
        stop: 'stopPrice',
        target: 'targetPrice',
      };

      updateTradeField(fieldMap[draggingLine], roundedPrice);
    }

    if (chartRef?.current) {
      chartRef.current.applyOptions({
        handleScroll: true,
        handleScale: true,
      });
    }

    setIsDragging(false);
    setDraggingLine(null);
    dragStartPriceRef.current = null;
  }, [isDragging, draggingLine, containerRef, chartRef, yToPrice, updateTradeField]);

  const handleTouchCancel = useCallback(() => {
    if (isDragging && draggingLine && dragStartPriceRef.current !== null) {
      const line = linesRef.current[draggingLine];
      if (line) {
        line.applyOptions({ price: dragStartPriceRef.current });
      }

      if (chartRef?.current) {
        chartRef.current.applyOptions({
          handleScroll: true,
          handleScale: true,
        });
      }
    }

    setIsDragging(false);
    setDraggingLine(null);
    dragStartPriceRef.current = null;
  }, [isDragging, draggingLine, chartRef]);

  // Set up mouse event listeners
  useEffect(() => {
    const container = containerRef?.current;
    if (!container || !chartReady || !currentTrade || isTouchDevice) return;

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [containerRef, chartReady, currentTrade, handleMouseMove, handleMouseDown, handleMouseUp, handleMouseLeave, isTouchDevice]);

  useEffect(() => {
    const container = containerRef?.current;
    if (!container || !chartReady || !currentTrade || !isTouchDevice) return;

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('touchcancel', handleTouchCancel);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [containerRef, chartReady, currentTrade, isTouchDevice, handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel]);

  useEffect(() => {
    if (!currentTrade) {
      setSelectedLine(null);
    }
  }, [currentTrade]);

  // Update lines when trade changes
  useEffect(() => {
    if (!chartReady || !candleSeriesRef?.current) {
      return;
    }

    // No trade - clear all lines
    if (!currentTrade) {
      clearLines();
      return;
    }

    // Don't update lines while dragging (we handle that separately)
    if (isDragging) return;

    // Update each line
    if (currentTrade.entryPrice) {
      const isLong = currentTrade.direction === Direction.LONG;
      updateLine('entry', currentTrade.entryPrice, {
        ...lineConfig.entry,
        title: `Entry ${isLong ? '▲' : '▼'}`,
      });
    }

    if (currentTrade.stopPrice) {
      updateLine('stop', currentTrade.stopPrice, lineConfig.stop);
    }

    if (currentTrade.targetPrice) {
      updateLine('target', currentTrade.targetPrice, lineConfig.target);
    }

  }, [currentTrade, chartReady, candleSeriesRef, clearLines, updateLine, lineConfig, isDragging]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearLines();
    };
  }, [clearLines]);

  return {
    hasTradeLines: Boolean(currentTrade),
    clearLines,
    isDragging,
    draggingLine,
    hoveringLine,
  };
};

export default useTradePriceLines;
